import { queryStringify } from '@/utils';
import axios, { AxiosInstance, AxiosRequestConfig, AxiosResponse, InternalAxiosRequestConfig } from 'axios';
import { handlePayment } from '@/framework/PaymentHandler'; // ✅ 新增导入

declare type ResponseResult = { code: number; data: any; message: string };
// 新增：流式请求的回调接口
export interface StreamOptions {
    onMessage: (text: string) => void;
    onDone?: () => void;
    onError?: (err: any) => void;
    signal?: AbortSignal; // 用于中断请求
}
class HttpUtils {
    private UriPrefix = `${import.meta.env.VITE_API_HOST}`;
    private readonly request: AxiosInstance;
    private mLogged = false;
    public constructor() {
        this.request = axios.create();
        this.request.defaults.headers['Content-Type'] = 'application/json;charset=UTF-8;';
        this.request.defaults.headers['Accept'] = 'application/json';
        this.request.interceptors.response.use(
            (o) => this.ParseResponseBodySuccess(o),
            (o) => this.ParseResponseBodyError(o),
        );
        this.request.defaults.baseURL = this.UriPrefix;
    }
    private Config: HttpConfig = {};

    public get Logged() {
        return this.mLogged;
    }

    private ParseResponseBodySuccess(response: AxiosResponse) {
        const data = response.data as ResponseResult;
        if (!data.code) {
            return Promise.reject(`data error ${data}`);
        }
        if (data.code != 200) {
            return Promise.reject(`data error ${data.message}`);
        }
        return Promise.resolve(data.data);
    }
    //兼容X402
    private async ParseResponseBodyError(error: any) {
        const { response, config } = error || {};
        if (response && response.status === 402) {
            const payment = response.data;
            console.warn('[X402] Payment Required:', payment);
            if (!payment) {
                return Promise.reject('Payment info missing');
            }
            try {
                // 使用策略模式进行支付
                const xPaymentHeader = await handlePayment(payment);
                console.log('[X402] Payment success, retrying original request...');
                config.headers = {
                    ...(config.headers || {}),
                    'X-PAYMENT': xPaymentHeader,
                };
                // 支付成功 -> 重试原请求
                return this.request(config);
            } catch (err) {
                console.error('[X402] Payment failed:', err);
                return Promise.reject('Payment failed or cancelled');
            }
        }
        // 其他错误
        return Promise.reject(error);
    }

    public UnLogin() {
        delete this.Config.Address;
        delete this.Config.WalletType;
        delete this.Config.Chain;
        delete this.Config.DAppNo;
        delete this.Config.Authorization;
        this.mLogged = false;
    }

    public Login(token: string) {
        this.Config.Authorization = token;
        if (this.Config.Authorization) {
            this.request.defaults.headers['X-Authorization'] = this.Config.Authorization;
        }
        this.mLogged = true;
    }

    public SetAccount(config: HttpConfig) {
        this.Config = { ...this.Config, ...(config || {}) };
        if (this.Config.Address) {
            this.request.defaults.headers['X-Wallet-Address'] = this.Config.Address;
        }
        if (this.Config.WalletType) {
            this.request.defaults.headers['X-Wallet-Type'] = this.Config.WalletType;
        }
        if (this.Config.Chain) {
            this.request.defaults.headers['X-Chain'] = this.Config.Chain;
        }
        if (this.Config.DAppNo) {
            this.request.defaults.headers['X-Dapp-No'] = this.Config.DAppNo;
        }
        if (this.Config.Language) {
            this.request.defaults.headers['X-Client-Language'] = this.Config.Language;
        }
        if (this.Config.CurrentTime) {
            this.request.defaults.headers['X-Client-Time'] = this.Config.CurrentTime;
        }
        if (this.Config.Authorization) {
            this.request.defaults.headers['X-Authorization'] = this.Config.Authorization;
        }
        if (this.Config.Timeout) {
            this.request.defaults.timeout = this.Config.Timeout;
        }
    }
    public SetUriPrefix(uriPrefix: string) {
        this.UriPrefix = uriPrefix;
        this.request.defaults.baseURL = this.UriPrefix;
    }
    // 辅助方法：获取合并后的 Headers 对象（供 fetch 使用）
    private GetMergedHeaders(customConfig: HttpConfig = {}): Record<string, string> {
        const headers: Record<string, string> = {
            'Content-Type': 'application/json;charset=UTF-8;',
            Accept: 'application/json',
        };
        // 1. 合并全局 Config
        const c = { ...this.Config, ...customConfig };
        if (c.Address) headers['X-Wallet-Address'] = c.Address;
        if (c.WalletType) headers['X-Wallet-Type'] = c.WalletType;
        if (c.Chain) headers['X-Chain'] = c.Chain;
        if (c.DAppNo) headers['X-Dapp-No'] = c.DAppNo;
        if (c.Language) headers['X-Client-Language'] = c.Language;
        if (c.CurrentTime) headers['X-Client-Time'] = c.CurrentTime.toString();
        if (c.Authorization) headers['X-Authorization'] = c.Authorization;
        if (c.ContentType) headers['Content-Type'] = c.ContentType;
        return headers;
    }
    private MergeConfig(c: HttpConfig): AxiosRequestConfig {
        const config = { headers: {} } as InternalAxiosRequestConfig;
        if (!c) {
            return config;
        }
        if (c.Address) {
            config.headers['X-Wallet-Address'] = c.Address;
        }
        if (c.WalletType) {
            config.headers['X-Wallet-Type'] = c.WalletType;
        }
        if (c.Chain) {
            config.headers['X-Chain'] = c.Chain;
        }
        if (c.DAppNo) {
            config.headers['X-Dapp-No'] = c.DAppNo;
        }
        if (c.Language) {
            config.headers['X-Client-Language'] = c.Language;
        }
        if (c.CurrentTime) {
            config.headers['X-Client-Time'] = c.CurrentTime;
        }
        if (c.Authorization) {
            config.headers['X-Authorization'] = c.Authorization;
        }
        if (c.Timeout) {
            config.timeout = c.Timeout;
        }
        if (c.ContentType) {
            config.headers['Content-Type'] = c.ContentType;
        }
        if (c.onUploadProgress) {
            config.onUploadProgress = c.onUploadProgress;
        }
        return config;
    }

    public Post(path: string, body: any, config: HttpConfig = {}) {
        return this.request.post(path, body, this.MergeConfig(config));
    }

    public Put(path: string, body: any, config: HttpConfig = {}) {
        return this.request.put(path, body, this.MergeConfig(config));
    }

    public Patch(path: string, body: any, config: HttpConfig = {}) {
        return this.request.patch(path, body, this.MergeConfig(config));
    }

    public Delete(path: string, config: HttpConfig = {}) {
        return this.request.delete(path, this.MergeConfig(config));
    }
    public Get(path: string, params: { [key in string]?: any } = {}, config: HttpConfig = {}) {
        // eslint-disable-next-line no-param-reassign
        path = path + (path.includes('?') ? '&' : '?') + queryStringify(params || {});
        // trim ?
        if (path.endsWith('?')) {
            // eslint-disable-next-line no-param-reassign
            path = path.substring(0, path.length - 1);
        }
        return this.request.get(path, this.MergeConfig(config));
    }
    /**
     * ✅ 新增：基于 fetch 的流式请求 (SSE)
     * 支持自动携带 Header 和 X402 重试（部分支持，fetch拦截比较复杂，这里实现基础鉴权）
     */
    public async FetchStream(path: string, body: any = null, config: HttpConfig = {}, streamOptions: StreamOptions) {
        const { onMessage, onDone, onError, signal } = streamOptions;
        // 1. 拼接完整 URL
        // 如果 path 已经是绝对路径则不动，否则拼接 UriPrefix
        const url = path.startsWith('http') ? path : `${this.UriPrefix}${path}`;
        // 2. 准备 Headers
        const headers = this.GetMergedHeaders(config);
        // 3. 准备 fetch 配置
        const fetchConfig: RequestInit = {
            method: body ? 'POST' : 'GET',
            headers: headers,
            signal: signal,
        };

        if (body) {
            fetchConfig.body = JSON.stringify(body);
        }

        try {
            const response = await fetch(url, fetchConfig);
            if (!response.ok) {
                // 尝试读取错误信息
                const errorText = await response.text();
                throw new Error(`HTTP ${response.status}: ${errorText}`);
            }
            if (!response.body) {
                throw new Error('Response body is null');
            }
            const reader = response.body.getReader();
            const decoder = new TextDecoder('utf-8');
            while (true) {
                const { done, value } = await reader.read();
                if (done) {
                    if (onDone) onDone();
                    break;
                }
                const chunk = decoder.decode(value, { stream: true });
                if (onMessage) {
                    onMessage(chunk);
                }
            }
        } catch (err: any) {
            if (err.name === 'AbortError') {
                console.log('Stream aborted by user');
            } else {
                if (onError) onError(err);
            }
        }
    }
}

export default new HttpUtils();
